import { ClipBoardTooltip } from "@/components/ClipBoardTooltip.tsx";
import { ContentPane } from "@/components/ContentPane.tsx";
import { LongDate } from "@/components/l10n/DateFormat.tsx";
import { NavigateAfterDelete } from "@/components/NavigateAfterDelete.tsx";
import { PriceWithDiscount } from "@/components/PriceWithDiscount.tsx";
import { ResourceWithIcon } from "@/components/ResourceWithIcon.tsx";
import { SectionHeader } from "@/components/SectionHeader.tsx";
import { Separator } from "@/components/Separator.tsx";
import { DetailsHolder, DetailsTable } from "@/components/table/DetailsTable.tsx";
import { DetailsTableBillingAccount } from "@/components/table/DetailsTableBillingAccount.tsx";
import { MonthlyCostRow } from "@/components/table/DetailsTableMonthlyCost.tsx";
import { DetailsTableName } from "@/components/table/DetailsTableName.tsx";
import { DetailsTableRow } from "@/components/table/DetailsTableRow.tsx";
import { WTable, WTableBody } from "@/components/table/WTable.tsx";
import { ViewTitle } from "@/components/Title.tsx";
import { Toolbar } from "@/components/Toolbar.tsx";
import { type WTabItem, WTabs } from "@/components/WTabs.tsx";
import { getResourceById } from "@/modules/api/resourceTypeException.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { description_snapshots } from "@/modules/compute/labels.tsx";
import type { VirtualMachineLoc } from "@/modules/compute/vmQuery.ts";
import { AttachedToSelect } from "@/modules/disks/AttachedToSelect.tsx";
import { DiskCloneModal } from "@/modules/disks/disk/DiskCloneModal.tsx";
import { DiskDeleteModal } from "@/modules/disks/disk/DiskDeleteModal.tsx";
import { DiskEstimatedReplicaPriceModal } from "@/modules/disks/disk/DiskEstimatedReplicaPriceModal.tsx";
import { getAttachedResourceId, getAttachedResourceIsPrimary } from "@/modules/disks/disk/resourceId.ts";
import { diskAttachableQueryAtom } from "@/modules/disks/disk/resourceQuery.ts";
import {
    attachDiskMutation,
    deleteDiskMutation,
    detachDiskMutation,
    type DiskWithPrice,
    diskWithPricesQueryAtom,
    modifyDiskMutation,
} from "@/modules/disks/disksListQuery.ts";
import { DiskStatusBadge } from "@/modules/disks/DiskStatusBadge.tsx";
import { LocationDetail } from "@/modules/location/LocationDetail.tsx";
import { resourceIdToString, type UNASSIGNED_RESOURCE } from "@/modules/network/ipAddress/resourceId.ts";
import { pricesAtom } from "@/modules/pricing/pricingQuery.ts";
import { getDiskAllSnapshotsPrice } from "@/modules/pricing/resourcePricing.ts";
import { useDeletableResource } from "@/utils/query/useDeletableResource.tsx";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import { apiDatetimeToDate } from "@warrenio/api-spec/conversion";
import type { BillingAccount } from "@warrenio/api-spec/spec.oats.gen";
import { filterFalse } from "@warrenio/utils/collections/filterFalse";
import { first, sortBy } from "remeda";

// NB: Extract the mutation call to a separate function so we can get its exact type via `typeof`
function useDeleteDiskMutation() {
    return useStandardMutation(deleteDiskMutation);
}

export type DeleteDiskMutation = ReturnType<typeof useDeleteDiskMutation>;

export function DiskView({ location, diskId }: { location: string; diskId: string }) {
    //#region Hooks
    const { t } = useLingui();
    const deleteMutation = useDeleteDiskMutation();
    const modifyMutation = useStandardMutation(modifyDiskMutation);
    const attachMutation = useStandardMutation(attachDiskMutation);
    const detachMutation = useStandardMutation(detachDiskMutation);

    const data = useSuspenseQueryAtom(diskWithPricesQueryAtom(location));
    const _vms = useSuspenseQueryAtom(diskAttachableQueryAtom(location));

    const obj = useDeletableResource(() => getResourceById(data, diskId, "disk"), deleteMutation);
    //#endregion

    if (obj === undefined) {
        return <NavigateAfterDelete />;
    }

    const {
        $type,
        billing_account_id,
        created_at,
        display_name,
        uuid,
        size_gb,
        storage_pool_uuid,
        attached_to,
        status,
    } = obj;

    const attachedResourceId = getAttachedResourceId(obj);
    const isPrimary = getAttachedResourceIsPrimary(obj);

    async function changeBillingAccount(billing_account: BillingAccount) {
        const updated = { billing_account_id: billing_account.id };
        await modifyMutation.mutateAsync({
            location,
            uuid,
            body: updated,
        });
    }

    async function renameDisk(display_name: string) {
        const updated = { display_name: display_name };
        await modifyMutation.mutateAsync({ location, uuid, body: updated });
    }

    async function detach() {
        if (!attachedResourceId) {
            return;
        }
        await detachMutation.mutateAsync({
            body: {
                uuid: attachedResourceId.$id,
                storage_uuid: uuid,
            },
            location,
        });
    }

    async function attachTo(value: VirtualMachineLoc | UNASSIGNED_RESOURCE) {
        if (value) {
            await attachMutation.mutateAsync({
                body: {
                    uuid: value.uuid,
                    storage_uuid: uuid,
                },
                location,
            });
        } else {
            await detach();
        }
    }

    const tabs = filterFalse<WTabItem>([
        {
            id: "snapshots",
            title: <Trans>Snapshots</Trans>,
            content: <SnapshotsContent disk={obj} />,
        },
    ]);

    return (
        <>
            <Toolbar>
                <DiskCloneModal disk={obj} />
                <DiskDeleteModal isDisabled={!!attached_to} item={obj} deleteMutation={deleteMutation} />
            </Toolbar>

            <ContentPane className="VStack gap-4">
                <ViewTitle title={display_name ? display_name : uuid} />

                <DetailsHolder>
                    <DetailsTable>
                        <DetailsTableName value={display_name} onChange={async (e) => await renameDisk(e)} />
                        <DetailsTableRow title={t`Type`}>
                            <ResourceWithIcon type={$type} />
                        </DetailsTableRow>
                        <DetailsTableRow title={t`Status`}>
                            <DiskStatusBadge value={status} />
                        </DetailsTableRow>
                        <DetailsTableRow title={t`Attached to`}>
                            <AttachedToSelect
                                enableInlineEditing
                                storagePoolUuid={storage_pool_uuid}
                                valueKey={resourceIdToString(attachedResourceId)}
                                location={location}
                                onChange={async (obj) => await attachTo(obj)}
                            />
                        </DetailsTableRow>
                        <DetailsTableRow title={t`Boot`}>{isPrimary ? <Trans>Yes</Trans> : "-"}</DetailsTableRow>
                        <DetailsTableRow title={t`Size`}>
                            {size_gb} <Trans>GB</Trans>
                        </DetailsTableRow>
                    </DetailsTable>

                    <DetailsTable>
                        <DetailsTableRow title={t`UUID`}>
                            <ClipBoardTooltip>{uuid}</ClipBoardTooltip>
                        </DetailsTableRow>
                        <DetailsTableRow title={t`Created`}>
                            <LongDate date={created_at} />
                        </DetailsTableRow>
                        <LocationDetail slug={location} />
                        <DetailsTableRow />
                        <DetailsTableBillingAccount
                            valueKey={billing_account_id}
                            onChange={async (item) => await changeBillingAccount(item.account)}
                        />
                        <MonthlyCostRow price={obj.price} billingAccountId={billing_account_id} />
                    </DetailsTable>
                </DetailsHolder>
            </ContentPane>

            <Separator />
            <WTabs autoSuspense allTab items={tabs} />
        </>
    );
}

function sortByCreatedAtDesc<T extends { created_at: string }>(items: T[]) {
    return sortBy(items, [(item) => apiDatetimeToDate(item.created_at).getTime(), "desc"]);
}

function SnapshotsContent({ disk }: { disk: DiskWithPrice }) {
    //#region Hooks
    const { t } = useLingui();
    const priceList = useSuspenseQueryAtom(pricesAtom);
    //#endregion

    const items = sortByCreatedAtDesc(disk.snapshots ?? []);
    const latestItem = first(items);

    const totalPrice = getDiskAllSnapshotsPrice(priceList, disk);

    return (
        <ContentPane className="VStack gap-4">
            <SectionHeader title={<Trans>Snapshots</Trans>} description={description_snapshots} />

            {items.length ? (
                <>
                    <DetailsTable>
                        <DetailsTableRow title={t`Est. monthly cost`}>
                            <PriceWithDiscount
                                price={totalPrice}
                                billingAccountId={disk.billing_account_id}
                                allowFree
                            />
                        </DetailsTableRow>
                        <DetailsTableRow title={t`Latest snapshot`}>
                            <LongDate date={latestItem!.created_at} />
                        </DetailsTableRow>
                    </DetailsTable>

                    <WTable>
                        <thead>
                            <tr>
                                <th>
                                    <Trans>UUID</Trans>
                                </th>
                                <th>
                                    <Trans>Size (GB)</Trans>
                                </th>
                                <th>
                                    <Trans>Created</Trans>
                                </th>
                                <th />
                            </tr>
                        </thead>
                        <WTableBody>
                            {items.map((item) => (
                                <tr key={item.uuid}>
                                    <td>
                                        <ClipBoardTooltip>{item.uuid}</ClipBoardTooltip>
                                    </td>
                                    <td>{item.size_gb}</td>
                                    <td>
                                        <LongDate date={item.created_at} />
                                    </td>
                                    <td className="text-right" />
                                </tr>
                            ))}
                        </WTableBody>
                    </WTable>
                </>
            ) : (
                <DiskEstimatedReplicaPriceModal
                    disk={disk}
                    type="snapshot"
                    billingAccountId={disk.billing_account_id}
                />
            )}
        </ContentPane>
    );
}
